import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import client from '../../api/client';
import SuburbSearch from '../../components/SuburbSearch';

export default function Register() {
    const { login }      = useAuth();
    const navigate       = useNavigate();
    const [searchParams] = useSearchParams();
    const [error,    setError]    = useState('');
    const [loading,  setLoading]  = useState(false);
    const [country,  setCountry]  = useState('AU');
    const [suburb,   setSuburb]   = useState(null);
    // /register?ref=CODE comes from a gardener's referral link, so
    // it prefills the code AND defaults the account type to
    // gardener — referral links are gardener-to-gardener. An
    // explicit ?type= still wins.
    const refCode = searchParams.get('ref') || '';

    const [form, setForm] = useState({
        user_type: searchParams.get('type') || (refCode ? 'gardener' : 'buyer'),
        first_name:'', last_name:'', email:'', password:'',
        phone:'', referred_by_code: refCode,
    });

    const handleChange = e => setForm({...form, [e.target.name]: e.target.value});

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!suburb) { setError('Please select your suburb.'); return; }
        setError(''); setLoading(true);
        try {
            await client.post('/users/register', {
                ...form,
                suburb_id: suburb.suburb_id,
            });
            const user = await login(form.email, form.password);
            if (user.user_type === 'gardener') navigate('/gardener/dashboard');
            else navigate('/buyer/dashboard');
        } catch (err) {
            setError(err.response?.data?.error || 'Registration failed.');
        } finally { setLoading(false); }
    };

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <h2 style={styles.title}>🌿 Create your account</h2>
                <div style={styles.toggle}>
                    <button style={form.user_type==='buyer' ? styles.toggleOn : styles.toggleOff}
                        type="button" onClick={() => setForm({...form, user_type:'buyer'})}>
                        I need a gardener
                    </button>
                    <button style={form.user_type==='gardener' ? styles.toggleOn : styles.toggleOff}
                        type="button" onClick={() => setForm({...form, user_type:'gardener'})}>
                        I am a gardener
                    </button>
                </div>
                {/* Was: "FREE 2-month trial" — left over from the old
                    subscription model that the credit system replaced.
                    It promised something the platform no longer offers. */}
                {form.user_type==='gardener' && (
                    <div style={styles.trial}>🎉 Join free and start with $10 credit — no card needed</div>
                )}
                {error && <div style={styles.error}>{error}</div>}
                <form onSubmit={handleSubmit}>
                    <div style={styles.row}>
                        <div style={styles.field}>
                            <label style={styles.label}>First name</label>
                            <input name="first_name" value={form.first_name} onChange={handleChange}
                                style={styles.input} placeholder="Bruce" required />
                        </div>
                        <div style={styles.field}>
                            <label style={styles.label}>Last name</label>
                            <input name="last_name" value={form.last_name} onChange={handleChange}
                                style={styles.input} placeholder="Green" required />
                        </div>
                    </div>
                    <div style={styles.field}>
                        <label style={styles.label}>Email</label>
                        <input type="email" name="email" value={form.email} onChange={handleChange}
                            style={styles.input} required />
                    </div>
                    <div style={styles.field}>
                        <label style={styles.label}>Password</label>
                        <input type="password" name="password" value={form.password} onChange={handleChange}
                            style={styles.input} required />
                    </div>
                    <div style={styles.field}>
                        <label style={styles.label}>Phone</label>
                        <input name="phone" value={form.phone} onChange={handleChange}
                            style={styles.input} placeholder="0412 345 678" />
                    </div>

                    {/* Suburb search with country selector */}
                    <div style={styles.field}>
                        <SuburbSearch
                            label="Your suburb"
                            value={suburb}
                            onChange={setSuburb}
                            country={country}
                            onCountryChange={setCountry}
                            placeholder="Start typing your suburb..."
                            showCountry={true}
                            required={true}
                        />
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>
                            Referral code <span style={{color:'#999'}}>(optional)</span>
                        </label>
                        <input name="referred_by_code" value={form.referred_by_code}
                            onChange={handleChange} style={styles.input} placeholder="e.g. BRUCE001" />
                        {refCode && form.referred_by_code === refCode && (
                            <div style={{fontSize:'0.78rem', color:'#2d6a4f', marginTop:'4px'}}>
                                Code applied from the link you followed.
                            </div>
                        )}
                    </div>

                    <button type="submit" style={styles.btn} disabled={loading}>
                        {loading ? 'Creating account...' : 'Create Account'}
                    </button>
                </form>
                <p style={styles.footer}>
                    Already have an account? <Link to="/login" style={styles.link}>Sign in</Link>
                </p>
            </div>
        </div>
    );
}

const styles = {
    page:      { minHeight:'80vh', display:'flex', alignItems:'center', justifyContent:'center', backgroundColor:'#f8fdf9', padding:'24px' },
    card:      { background:'white', borderRadius:'12px', padding:'40px', width:'100%', maxWidth:'520px', boxShadow:'0 4px 20px rgba(0,0,0,0.1)' },
    title:     { margin:'0 0 20px', color:'#2d6a4f', fontSize:'1.6rem', textAlign:'center' },
    toggle:    { display:'flex', marginBottom:'20px', borderRadius:'8px', overflow:'hidden', border:'1px solid #2d6a4f' },
    toggleOn:  { flex:1, padding:'10px', backgroundColor:'#2d6a4f', color:'white', border:'none', cursor:'pointer', fontWeight:'bold' },
    toggleOff: { flex:1, padding:'10px', backgroundColor:'white', color:'#2d6a4f', border:'none', cursor:'pointer' },
    trial:     { backgroundColor:'#e8f5e9', border:'1px solid #a5d6a7', borderRadius:'6px', padding:'10px', textAlign:'center', marginBottom:'16px', fontSize:'0.9rem', color:'#2d6a4f' },
    error:     { backgroundColor:'#fee', border:'1px solid #fcc', borderRadius:'6px', padding:'10px', color:'#c00', marginBottom:'16px', fontSize:'0.9rem' },
    row:       { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' },
    field:     { marginBottom:'14px' },
    label:     { display:'block', marginBottom:'5px', fontWeight:'500', color:'#333', fontSize:'0.9rem' },
    input:     { width:'100%', padding:'10px 12px', border:'1px solid #ddd', borderRadius:'6px', fontSize:'1rem', boxSizing:'border-box' },
    btn:       { width:'100%', padding:'12px', backgroundColor:'#2d6a4f', color:'white', border:'none', borderRadius:'6px', fontSize:'1rem', fontWeight:'bold', cursor:'pointer', marginTop:'8px' },
    footer:    { textAlign:'center', marginTop:'20px', color:'#666', fontSize:'0.9rem' },
    link:      { color:'#2d6a4f', fontWeight:'bold' },
};
